import React, { useState } from 'react';
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendEmailVerification as firebaseSendEmailVerification,
    sendPasswordResetEmail,
    updatePassword,
    setPersistence,
    browserLocalPersistence,
    User
} from 'firebase/auth';
import { useFirebase } from '../firebase/firebase-provider';

interface AuthContextType {
    emailAndPasswordRegister: (email: string, password: string) => Promise<unknown>;
    emailAndPasswordLogin: (email: string, password: string) => Promise<unknown>;
    logOut: () => Promise<void>;
    passwordReset: (email: string) => Promise<void>;
    passwordUpdate: (password: string) => Promise<void>;
    isAuthenticated: () => boolean;
    isVerified: () => boolean;
    sendEmailVerification: () => Promise<void>;
    currentUser: User | null | undefined;
    initializeApp: () => void;
    initialized: boolean;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = React.useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const actionCodeSettings = {
    // URL you want to redirect back to. The domain (www.example.com) for this
    // URL must be whitelisted in the Firebase Console.
    url: `${window.location.origin}/login`,
    // This must be true.
    handleCodeInApp: true,
};

export function AuthProvider(props: any) {
    const [currentUser, setCurrentUser] = useState<User | null | undefined>(undefined);
    const [initialized, setInitialized] = useState(false);

    const app = useFirebase();
    const auth = getAuth(app!);
    onAuthStateChanged(auth, (user: User | null) => {
        updateCurrentUser(user);
    });

    function updateCurrentUser(user: any) {
        setCurrentUser(user);
    }

    function initializeApp() {
        if (initialized) {
            return;
        }
        const unsubscribe = onAuthStateChanged(auth, (user: any) => {
            unsubscribe();
            setCurrentUser(user);
            setInitialized(true);
            onAuthStateChanged(auth, setCurrentUser);
        });
    }

    function emailAndPasswordRegister(email: any, password: any) {
        return new Promise((resolve, reject) => {
            setPersistence(auth, browserLocalPersistence).then(() => {
                createUserWithEmailAndPassword(auth, email, password)
                    .then((newUser: any) => {
                        updateCurrentUser(newUser.user);
                        newUser.user.reload().then(() => {
                            sendEmailVerification().then(resolve).catch(reject);
                        });
                    }).catch(reject);
            });
        });
    }

    function sendEmailVerification() {
        return firebaseSendEmailVerification(auth.currentUser, actionCodeSettings);
    }

    function emailAndPasswordLogin(email: any, password: any) {
        return new Promise((resolve, reject) => {
            signInWithEmailAndPassword(auth, email, password).then((userCredentials: any) => {
                updateCurrentUser(userCredentials.user);
                resolve(userCredentials.user);
            }).catch(reject);
        });
    }

    function logOut() {
        return signOut(auth).then(() => {
            updateCurrentUser(undefined);
        });
    }

    function passwordReset(email: any) {
        return sendPasswordResetEmail(auth, email);
    }

    function passwordUpdate(password: any) {
        return updatePassword(auth.currentUser, password);
    }

    function isAuthenticated() {
        return !!auth.currentUser;
    }

    function isVerified() {
        return isAuthenticated() && auth.currentUser.emailVerified;
    }

    return (
        <AuthContext.Provider
            value={{
                emailAndPasswordRegister,
                emailAndPasswordLogin,
                logOut,
                passwordReset,
                passwordUpdate,
                isAuthenticated,
                isVerified,
                sendEmailVerification,
                currentUser,
                initializeApp,
                initialized,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
}
